import { useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../../axios.config";
import { fetchStepFilesContent, fetchStepBgImgFileContent, createBlobUrls } from "./utils/commonUtils";
import { buildSaveStepContentFormData } from "./utils/useCourseDetailsHandlersUtils";
import * as courseDetailsService from "../../services/courseDetails.service";

export const useCourseDetailsHandlers = (
    lngsltd,
    selectedStep,
    mediaInputRef,
    stepContentState,
    setSelectedStep,
    fetchCourseSteps,
    setStepContentState,
    isUploading,
    setIsUploading
) => {
    const [backgroundImageFile, setBackgroundImageFile] = useState(null);
    const [backgroundImageUrl, setBackgroundImageUrl] = useState("");
    const [backgroundImageUrlStep, setBackgroundImageUrlStep] = useState("");

    // File size limits in bytes
    const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
    const MAX_VIDEO_SIZE = 500 * 1024 * 1024; // 500MB

    const validateFileSize = (files, isVideo = false) => {
        const maxSize = isVideo ? MAX_VIDEO_SIZE : MAX_IMAGE_SIZE;
        const fileType = isVideo ? "Video" : "Image";
        
        for (const file of files) {
            if (file.size > maxSize) {
                const maxSizeMB = Math.round(maxSize / (1024 * 1024));
                toast.error(`${fileType} size must be less than ${maxSizeMB}MB`);
                return false;
            }
        }
        return true;
    };

    const handleStepClick = async (step) => {
        // Prevent double execution
        if (selectedStep?.stepNo === step.stepNo) {
            return;
        }

        if (!step || !step.courseID || !step.contentType) {
            console.error('Invalid step data:', step);
            return;
        }

        setSelectedStep(step);

        try {
            let parsedDesObj = {};
            
            if (step.description) {
                try {
                    parsedDesObj = JSON.parse(step.description);
                } catch {
                    parsedDesObj = { en: step.description, no: "" };
                }
            }

            if (step.stepBgImgFileName && step.stepBgImgFileName.trim()) {
                try {
                    const bgImageResponse = await fetchStepBgImgFileContent(step.stepBgImgFileName, step);
                    if (bgImageResponse) {
                        const bgImageUrl = createBlobUrls([bgImageResponse], "image/jpeg");
                        setBackgroundImageUrl(bgImageUrl[0]);
                        setBackgroundImageUrlStep(step.stepBgImgFileName);
                    } else {
                        setBackgroundImageUrl("");
                        setBackgroundImageUrlStep("");
                    }
                } catch (error) {
                    console.error('Failed to load background image:', error);
                    setBackgroundImageUrl("");
                    setBackgroundImageUrlStep("");
                }
            } else {
                setBackgroundImageUrl("");
                setBackgroundImageUrlStep("");
            }

            let newState = {
                englishDescriptionContent: parsedDesObj.en,
                norwegianDescriptionContent: parsedDesObj.no,
                imageFiles: [],
                imageUrls: [],
                imageFileNames: [],
                videoFileNames: [],
                videoFiles: [],
                videoUrls: [],
            };

            // Handle Image content with strict validation
            if (step.contentType === "Image" && step.stepContent) {
                let cleanContent = step.stepContent.trim();
                
                if (cleanContent) {
                    // Remove quotes
                    if ((cleanContent.startsWith('"') && cleanContent.endsWith('"')) || 
                        (cleanContent.startsWith("'") && cleanContent.endsWith("'"))) {
                        cleanContent = cleanContent.slice(1, -1).trim();
                    }
                    
                    if (cleanContent) {
                        // Strict filtering - no empty strings, no undefined/null values
                        const validFileNames = cleanContent
                            .split(",")
                            .map(s => s.trim())
                            .filter(s => s && s.length > 0 && s !== 'undefined' && s !== 'null' && s !== '');
                        
                        if (validFileNames.length > 0) {
                            const responses = await fetchStepFilesContent(validFileNames, step);
                            const blobUrls = createBlobUrls(responses, "image/jpeg");
                            
                            newState = {
                                ...newState,
                                activeContentType: "Image",
                                imageFileNames: validFileNames,
                                imageUrls: blobUrls,
                                imageFiles: [],
                                hasNewFiles: false
                            };
                        } else {
                            newState.activeContentType = "Image";
                        }
                    } else {
                        newState.activeContentType = "Image";
                    }
                } else {
                    newState.activeContentType = "Image";
                }
            } 
            // Handle Video content with strict validation
            else if (step.contentType === "Video" && step.stepContent) {
                let cleanContent = step.stepContent.trim();
                
                if (cleanContent) {
                    // Remove quotes
                    if ((cleanContent.startsWith('"') && cleanContent.endsWith('"')) || 
                        (cleanContent.startsWith("'") && cleanContent.endsWith("'"))) {
                        cleanContent = cleanContent.slice(1, -1).trim();
                    }
                    
                    if (cleanContent) {
                        // Strict filtering for video files
                        const validFileNames = cleanContent
                            .split(",")
                            .map(s => s.trim())
                            .filter(s => s && s.length > 0 && s !== 'undefined' && s !== 'null' && s !== '');
                        
                        if (validFileNames.length > 0) {
                            // For videos, use only the last filename
                            const lastVideoFileName = validFileNames[validFileNames.length - 1];
                            const responses = await fetchStepFilesContent([lastVideoFileName], step);
                            const blobUrls = createBlobUrls(responses, "video/mp4");

                            newState = {
                                ...newState,
                                activeContentType: "Video",
                                videoFileNames: [lastVideoFileName], // Store filename for deletion
                                videoUrls: blobUrls,
                                videoFiles: [],
                                hasNewFiles: false
                            };
                        } else {
                            newState.activeContentType = "Video";
                        }
                    } else {
                        newState.activeContentType = "Video";
                    }
                } else {
                    newState.activeContentType = "Video";
                }
            }

            // Apply all state updates to stepContentState
            const updatedState = {
                ...stepContentState, // Preserve existing state
                englishDescriptionContent: newState.englishDescriptionContent || "",
                norwegianDescriptionContent: newState.norwegianDescriptionContent || "",
                imageFiles: newState.imageFiles || [],
                imageUrls: newState.imageUrls || [],
                imageFileNames: newState.imageFileNames || [],
                videoFileNames: newState.videoFileNames || [],
                videoFiles: newState.videoFiles || [],
                videoUrls: newState.videoUrls || [],
                activeContentType: newState.activeContentType || step.contentType || "",
                ContentType: step.contentType,
                hasNewFiles: newState.hasNewFiles || false,
                descriptionBgImgFile: backgroundImageFile
            };
            
            setStepContentState(updatedState);

        } catch (error) {
            console.error('Error loading step:', error);
            toast.error(lngsltd["No Data"]);
        }
    };

    const saveStepContent = async () => {
        if (!selectedStep) return;

        // Validate file sizes before upload
        if (stepContentState.hasNewFiles) {
            if (stepContentState.imageFiles.length > 0 && !validateFileSize(stepContentState.imageFiles, false)) return;
            if (stepContentState.videoFiles.length > 0 && !validateFileSize(stepContentState.videoFiles, true)) return;
        }

        setIsUploading(true);

        try {
            // Use activeContentType if ContentType is not set or doesn't match
            // This ensures the correct ContentType is used when user switches between Image and Video
            const contentTypeToSave = stepContentState.ContentType || stepContentState.activeContentType || selectedStep.contentType;
            
            // Update ContentType in state to match activeContentType if they differ
            if (stepContentState.activeContentType && stepContentState.ContentType !== stepContentState.activeContentType) {
                setStepContentState(prev => ({ ...prev, ContentType: prev.activeContentType }));
            }

            const formData = buildSaveStepContentFormData(stepContentState);

            const response = await axiosInstance.post(`/CourseStep/UploadStepMediaFiles?CourseID=${selectedStep.courseID}&StepNo=${selectedStep.stepNo}&StepTitle=${encodeURIComponent(selectedStep.stepTitle || '')}&ContentType=${contentTypeToSave}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            toast.success(lngsltd["Saved Successfully"]);
            
            // Reset upload state
            setStepContentState(prev => ({
                ...prev,
                hasNewFiles: false,
                imageFiles: [],
                videoFiles: []
            }));
            
            // Refresh data
            await fetchCourseSteps();

        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Upload failed";
            toast.error(errorMessage);
        } finally {
            setIsUploading(false);
        }
    };

    const handleImageChange = (event) => {
        const files = Array.from(event.target.files);
        if (files.length > 0) {
            if (validateFileSize(files, false)) {
                const urls = files.map(file => URL.createObjectURL(file));
                setStepContentState(prev => ({
                    ...prev,
                    imageFiles: files,
                    imageUrls: urls,
                    hasNewFiles: true
                }));
            }
        }
    };

    const handleVideoChange = (event) => {
        const files = Array.from(event.target.files);
        if (files.length > 0) {
            if (validateFileSize(files, true)) {
                const urls = files.map(file => URL.createObjectURL(file));
                setStepContentState(prev => ({
                    ...prev,
                    videoFiles: files,
                    videoUrls: urls,
                    hasNewFiles: true
                }));
            }
        }
    };

    const handleBackgroundImageChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            if (validateFileSize([file], false)) {
                setBackgroundImageFile(file);
                const url = URL.createObjectURL(file);
                setBackgroundImageUrl(url);
            }
        }
    };

    const handleMediaUpload = (event) => {
        if (stepContentState.activeContentType === "Image") {
            handleImageChange(event);
        } else if (stepContentState.activeContentType === "Video") {
            handleVideoChange(event);
        }
    };

    const handleVideoUpload = handleVideoChange;

    const handleImageButtonClick = () => {
        setStepContentState(prev => ({ 
            ...prev, 
            activeContentType: "Image",
            ContentType: "Image" // Update ContentType when user selects Image
        }));
    };

    const handleVideoButtonClick = () => {
        setStepContentState(prev => ({ 
            ...prev, 
            activeContentType: "Video",
            ContentType: "Video" // Update ContentType when user selects Video
        }));
    };

    const removeMedia = async (index) => {
        if (stepContentState.activeContentType === "Image") {
            const fileNameToDelete = stepContentState.imageFileNames[index];
            
            // Delete from Azure and database
            if (fileNameToDelete) {
                try {
                    await courseDetailsService.deleteStepMedia(
                        selectedStep.courseID,
                        selectedStep.stepNo,
                        'Image',
                        fileNameToDelete
                    );
                    toast.success("Image deleted successfully");
                } catch (error) {
                    console.error("Error deleting image:", error);
                    toast.error("Failed to delete image");
                    return;
                }
            }
            
            // Update UI state
            setStepContentState(prev => ({
                ...prev,
                imageFiles: prev.imageFiles.filter((_, i) => i !== index),
                imageUrls: prev.imageUrls.filter((_, i) => i !== index),
                imageFileNames: prev.imageFileNames.filter((_, i) => i !== index)
            }));
        } else if (stepContentState.activeContentType === "Video") {
            const fileNameToDelete = stepContentState.videoFileNames?.[0];
            
            // Delete from Azure and database
            if (fileNameToDelete) {
                try {
                    await courseDetailsService.deleteStepMedia(
                        selectedStep.courseID,
                        selectedStep.stepNo,
                        'Video',
                        fileNameToDelete
                    );
                    toast.success("Video deleted successfully");
                } catch (error) {
                    console.error("Error deleting video:", error);
                    toast.error("Failed to delete video");
                    return;
                }
            }
            
            // Update UI state
            setStepContentState(prev => ({
                ...prev,
                videoFileNames: [],
                videoFiles: [],
                videoUrls: []
            }));
        }
    };

    const handleDelete = async (step) => {
        if (window.confirm(`Delete step ${step.stepNo}?`)) {
            try {
                await axiosInstance.delete(`/CourseStep/${step.id}`);
                toast.success(lngsltd["Deleted Successfully"]);
                await fetchCourseSteps();
            } catch (error) {
                toast.error("Delete failed");
            }
        }
    };

    return {
        handleStepClick,
        handleImageButtonClick,
        handleVideoButtonClick,
        handleMediaUpload,
        handleVideoUpload,
        saveStepContent,
        removeMedia,
        handleDelete,
    };
};